import { api, unwrap } from '@/shared/lib/api';

export type CategoryKind =
  | 'instagram_verification'
  | 'telegram_premium'
  | 'telegram_stars'
  | 'telegram_gifts'
  | 'telegram_nft'
  | 'telegram_verification';

export interface Category {
  id: string;
  kind: CategoryKind;
  slug: string;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  descriptionUz: string | null;
  descriptionRu: string | null;
  descriptionEn: string | null;
  iconUrl: string | null;
  accentColor: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface Product {
  id: string;
  categoryId: string;
  sku: string;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  descriptionUz: string | null;
  descriptionRu: string | null;
  descriptionEn: string | null;
  imageUrl: string | null;
  price: string; // serialized decimal (USD mahsulotda dollar qiymati)
  oldPrice: string | null;
  // Server kurs bo'yicha hisoblagan UZS ekvivalenti — to'lov shu summada.
  priceUzs?: string;
  oldPriceUzs?: string | null;
  quantity: number | null;
  durationDays: number | null;
  requiresInstagramUsername: boolean;
  requiresTelegramUsername: boolean;
  stock: number | null;
  isFeatured: boolean;
  sortOrder: number;
  // Free-form flags. `vip` switches the card to the premium VIP design;
  // `currency: 'USD'` renders prices with a $ sign; `unlimited` shows "cheksiz".
  meta?: {
    vip?: boolean;
    currency?: 'USD' | 'UZS';
    unlimited?: boolean;
  } | null;
  category?: { slug: string; kind: CategoryKind; accentColor: string | null };
}

export interface CategoryWithProducts extends Category {
  products: Product[];
}

export interface Banner {
  id: string;
  titleUz: string;
  titleRu: string;
  titleEn: string;
  imageUrl: string;
  linkUrl: string | null;
}

export const catalogService = {
  async listCategories(): Promise<Category[]> {
    const res = await api.get('/categories');
    return unwrap<Category[]>(res.data);
  },
  async getCategory(slug: string): Promise<CategoryWithProducts> {
    const res = await api.get(`/categories/${slug}`);
    return unwrap<CategoryWithProducts>(res.data);
  },
  async listProducts(params?: {
    categorySlug?: string;
    featured?: boolean;
    limit?: number;
    q?: string;
  }) {
    const res = await api.get('/products', {
      params: {
        categorySlug: params?.categorySlug,
        featured: params?.featured ? 'true' : undefined,
        limit: params?.limit,
        q: params?.q,
      },
    });
    return unwrap<{ items: Product[]; nextCursor: string | null }>(res.data);
  },
  async getProduct(id: string): Promise<Product> {
    const res = await api.get(`/products/${id}`);
    return unwrap<Product>(res.data);
  },
  async listBanners(): Promise<Banner[]> {
    const res = await api.get('/banners');
    return unwrap<Banner[]>(res.data);
  },
};
